import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  Search,
  Mic,
  ShieldCheck,
  User,
  Pill,
  Plus,
  Trash2,
  Users,
} from 'lucide-react'
import { PatientBanner } from './PatientBanner'
import { Documents, RecentEncounters } from './ClinicalPanels'
import { VitalsPanel } from './VitalsPanel'
import { SpeakEncounterDock } from './SpeakEncounterDock'
import { RXNORM_FALLBACK } from '../data/rxnorm-fallback'
import {
  ApiError,
  addFamilyHistoryRelative,
  createMedication,
  deleteFamilyHistoryRelative,
  deleteMedication,
  getProviderPatient,
  listFamilyHistory,
  listPatientMedications,
  searchProviderRoster,
  updateProviderPatient,
  type FamilyHistoryRelative,
  type MedicationStatement,
  type PatientUpdatePayload,
  type ProviderRosterRow,
} from '../api/provider'
import { termsApi, type PatientResponse, type TermResult } from '../lib/api'
import { demoText, fmt, sexLabel, t, tierShortLabel } from '../lib/i18n'
import {
  SAMPLE_CHART_PATIENT_ID,
  SAMPLE_ROSTER,
  patientResponseToRosterRow,
  rosterRowToPatientResponse,
} from '../lib/samples/roster'
import {
  readOfflineFamilyHistory,
  readOfflineMeds,
  readOfflineRegistrations,
  readPatientEdits,
  writeOfflineFamilyHistory,
  writeOfflineMeds,
  writePatientEdits,
  type OfflineFamilyHistoryRelative,
  type OfflineMedication,
} from '../lib/offlineStore'

/**
 * ProviderExperience — fully live provider experience.
 *
 * - Roster: GET /api/v1/provider/roster with 250ms debounce + AbortController.
 * - Identity: diff-based PATCH /api/v1/patients/{id} (≥1 name enforced client-side).
 * - Medications: /api/v1/medications CRUD with RxNorm lookup + clinician provenance.
 * - Family history: /api/v1/family-history CRUD with SNOMED condition lookup.
 *
 * No fabricated rows when the API IS reachable: every list is server-loaded
 * and every write reflects the server response. When the deployment is static
 * (API unreachable), the affected sections fall back to bundled contract-exact
 * samples with a "Sample data — offline demo" badge, and offline writes persist
 * to localStorage — real API data always wins when the backend is reachable.
 */

/** Shared roster loader: live ILIKE search, offline fallback over samples. */
function useProviderRoster() {
  const [rows, setRows] = useState<ProviderRosterRow[]>([])
  const [offline, setOffline] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  // Abort in-flight roster requests whenever the component unmounts. Query
  // filtering happens at the call sites (live: server ILIKE; offline: local).
  const runSearch = (query: string) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setLoading(true)
    setError(null)
    searchProviderRoster(query, { signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted) return
        // Real API data wins when the backend is reachable.
        setRows(result)
        setOffline(false)
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return
        // Static-site fallback: bundled roster filtered in-memory + offline
        // registrations merged in, badged as sample data.
        if (err instanceof ApiError || err instanceof TypeError) {
          const status = err instanceof ApiError ? err.status : undefined
          const staticHost =
            typeof status === 'number' && (status === 404 || status === 405 || status === 501)
          if (err instanceof TypeError || staticHost) {
            const q = query.trim().toLowerCase()
            const registrations = readOfflineRegistrations()
            const offlineRows = registrations.map((entry) => ({
              ...patientResponseToRosterRow(entry.patient),
              registration_source: 'offline-registration',
            }))
            const pool = [...offlineRows, ...SAMPLE_ROSTER]
            const filtered = q
              ? pool.filter((row) =>
                  [
                    row.nhi,
                    row.mrn,
                    row.name_ar,
                    row.name_latin,
                    row.first_name,
                    row.last_name,
                    row.phone,
                    row.dob,
                  ]
                    .filter(Boolean)
                    .some((field) => String(field).toLowerCase().includes(q)),
                )
              : pool
            setRows(filtered)
            setOffline(true)
            setLoading(false)
            return
          }
        }
        setRows([])
        setError(err instanceof ApiError ? String(err.status) : 'network')
        setLoading(false)
      })
      .finally(() => {
        // Success path must always clear the loading flag (HEAD regressed on
        // this once — a stuck "Loading roster…" row with a correct 1-patient
        // count was the symptom). Aborted runs leave loading for the next run.
        if (!controller.signal.aborted) setLoading(false)
      })
  }

  return { rows, offline, loading, error, runSearch, abortRef }
}

/* ======================= Offline sample badge ======================= */

/** URL-safe unique id for offline-created rows (crypto.randomUUID with fallback). */
function cryptoId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `off-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function OfflineSampleBadge() {
  return (
    <span
      className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
      data-testid="sample-data-badge"
    >
      {t('prov.offlineSample')}
    </span>
  )
}

function toneForTier(tier: number) {
  if (tier >= 4) return 'bg-emerald-50 text-emerald-800 border-emerald-200'
  if (tier === 3) return 'bg-teal-50 text-teal-800 border-teal-200'
  if (tier === 2) return 'bg-amber-50 text-amber-800 border-amber-200'
  return 'bg-stone-100 text-stone-600 border-stone-200'
}

export function ProviderExperience() {
  const params = useParams()
  const navigate = useNavigate()
  const patientId = params.patientId

  return patientId ? (
    <ProviderPatientDetailPage patientId={patientId} onBack={() => navigate('/provider')} />
  ) : (
    <ProviderRosterPage onOpenPatient={(id) => navigate(`/provider/patients/${id}`)} />
  )
}

/* ============================== Roster ============================== */

function ProviderRosterPage({ onOpenPatient }: { onOpenPatient: (id: string) => void }) {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const { rows, offline, loading, error, runSearch } = useProviderRoster()

  // 250ms debounce between keystrokes and the live ILIKE search.
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query), 250)
    return () => window.clearTimeout(handle)
  }, [query])

  useEffect(() => {
    runSearch(debouncedQuery)
    return () => {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery])

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary-50 text-primary-700 ring-1 ring-primary-200">
              <User className="h-6 w-6" />
            </div>
            <div>
              <div className="text-sm font-medium text-stone-500">{t('prov.roster')}</div>
              <h2 className="text-3xl font-semibold text-stone-950">{t('prov.allPatients')}</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
                {t('prov.rosterDesc')}
              </p>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-900">
            <ShieldCheck className="h-4 w-4" />
            {t('prov.editableProvider')}
          </div>
        </div>
        {offline && (
          <p
            className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800"
            role="status"
          >
            <OfflineSampleBadge />
            {t('prov.offlineNote')}
          </p>
        )}
      </section>

      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
        <div className="mb-4 flex items-center gap-2">
          <Search className="h-5 w-5 text-primary-700" />
          <h3 className="text-lg font-semibold text-stone-950">{t('prov.searchRoster')}</h3>
        </div>
        <label className="grid gap-2 text-sm font-medium text-stone-700">
          {t('prov.searchAll')}
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('prov.searchHint')}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
      </section>

      <section className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-soft">
        <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
          <h3 className="font-semibold text-stone-950">{t('prov.patientList')}</h3>
          <span className="rounded-md bg-stone-100 px-2 py-0.5 text-xs font-semibold text-stone-600">
            {fmt(t(rows.length === 1 ? 'prov.onePatient' : 'prov.manyPatients'), { n: rows.length })}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-stone-50 text-stone-500">
              <tr>
                <th className="px-5 py-3 font-medium">{t('prov.name')}</th>
                <th className="px-5 py-3 font-medium">{t('prov.nhi')}</th>
                <th className="px-5 py-3 font-medium">{t('prov.mrn')}</th>
                <th className="px-5 py-3 font-medium">{t('prov.dobShort')}</th>
                <th className="px-5 py-3 font-medium">{t('prov.verification')}</th>
                <th className="px-5 py-3 font-medium">{t('prov.updatedAt')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-stone-500">
                    {t('prov.loadingRoster')}
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-red-700">
                    {t('prov.rosterFailed')}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-sm text-stone-500">
                    {t('prov.rosterEmpty')}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.patient_id}
                    className="cursor-pointer transition hover:bg-emerald-50/60"
                    onClick={() => onOpenPatient(row.patient_id)}
                    role="button"
                    tabIndex={0}
                    aria-label={fmt(t('prov.openPatientAria'), { name: row.name_latin })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onOpenPatient(row.patient_id)
                      }
                    }}
                  >
                    <td className="px-5 py-4">
                      <div className="font-medium text-stone-900">{row.name_latin || row.name_ar}</div>
                      <div className="text-xs text-stone-500">{row.name_ar}</div>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 font-mono text-emerald-800">{row.nhi}</td>
                    <td className="whitespace-nowrap px-5 py-4 font-mono text-stone-700">{row.mrn}</td>
                    <td className="whitespace-nowrap px-5 py-4 text-stone-700">{row.dob}</td>
                    <td className="whitespace-nowrap px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-semibold ${toneForTier(row.verification_tier)}`}>
                        <ShieldCheck className="h-3.5 w-3.5" />
                        {fmt(t('banner.level'), { n: row.verification_tier, name: tierShortLabel(row.verification_tier) })}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-stone-700">
                      {row.updated_at ? row.updated_at.slice(0, 10) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

/* ============================ Detail page ============================ */

type IdentityDraft = {
  name_ar: string
  name_latin: string
  dob: string
  sex: '' | 'M' | 'F'
  phone: string
  city: string
  governorate: string
}

function draftFromPatient(patient: PatientResponse): IdentityDraft {
  const person = patient.person
  return {
    name_ar: person.name_ar ?? '',
    name_latin: person.name_latin ?? '',
    dob: person.dob ?? '',
    sex: person.sex === 'M' || person.sex === 'F' ? person.sex : '',
    phone: person.phone ?? '',
    city: '',
    governorate: '',
  }
}

function ProviderPatientDetailPage({
  patientId,
  onBack,
}: {
  patientId: string
  onBack: () => void
}) {
  const [patient, setPatient] = useState<PatientResponse | null>(null)
  const [draft, setDraft] = useState<IdentityDraft | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [loadOffline, setLoadOffline] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [savedOffline, setSavedOffline] = useState(false)
  const [scribeOpen, setScribeOpen] = useState(false)
  const [encountersRefresh, setEncountersRefresh] = useState(0)
  const [encounterFocus, setEncounterFocus] = useState<string | null>(null)
  const [scribeToast, setScribeToast] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(false)
    setLoadOffline(false)
    getProviderPatient(patientId)
      .then((detail) => {
        if (cancelled) return
        setPatient(detail)
        setDraft(draftFromPatient(detail))
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // Static-site fallback: resolve the record from the bundled roster
        // (badged sample data) before giving up with the honest error state.
        const status = err instanceof ApiError ? err.status : undefined
        const staticHost =
          typeof status === 'number' && (status === 404 || status === 405 || status === 501)
        if (err instanceof TypeError || staticHost) {
          // Offline registrations (created on this device) win over bundled
          // samples; bundled samples resolve by exact patient_id match.
          const registered = readOfflineRegistrations().find(
            (entry) => entry.patient.id === patientId,
          )?.patient
          const rosterRow = SAMPLE_ROSTER.find((candidate) => candidate.patient_id === patientId)
          const detail = registered ?? (rosterRow ? rosterRowToPatientResponse(rosterRow) : null)
          if (detail) {
            // Overlay locally saved field edits on top of the base record.
            const edits = readPatientEdits(patientId)
            const merged: PatientResponse = {
              ...detail,
              person: {
                ...detail.person,
                name_ar: edits.name_ar ?? detail.person.name_ar ?? null,
                name_latin: edits.name_latin ?? detail.person.name_latin ?? null,
                dob: edits.dob ?? detail.person.dob ?? null,
                sex: edits.sex ?? detail.person.sex ?? null,
                phone: edits.phone ?? detail.person.phone ?? null,
              },
            }
            setPatient(merged)
            setDraft(draftFromPatient(merged))
            setLoadOffline(true)
            return
          }
        }
        setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [patientId])

  async function submitIdentity(event: FormEvent) {
    event.preventDefault()
    if (!patient || !draft) return
    if (!draft.name_ar.trim() && !draft.name_latin.trim()) {
      setSaveError('name-required')
      return
    }

    // Diff against the loaded record so the PATCH carries changed fields only.
    const person = patient.person
    const payload: PatientUpdatePayload = {}
    if (draft.name_ar.trim() !== (person.name_ar ?? '')) payload.name_ar = draft.name_ar.trim()
    if (draft.name_latin.trim() !== (person.name_latin ?? '')) payload.name_latin = draft.name_latin.trim()
    if (draft.dob !== (person.dob ?? '')) payload.dob = draft.dob
    if (draft.sex && draft.sex !== (person.sex ?? '')) payload.sex = draft.sex
    if (draft.phone.trim() !== (person.phone ?? '')) payload.phone = draft.phone.trim()

    if (Object.keys(payload).length === 0) {
      setSaveError(null)
      return
    }

    setSaving(true)
    setSaveError(null)
    setSavedOffline(false)
    try {
      // Saved-state comes from the server response, never from the local draft.
      const updated = await updateProviderPatient(patient.id, payload)
      setPatient(updated)
      setDraft(draftFromPatient(updated))
      setSavedAt(new Date().toISOString())
    } catch (err) {
      // Static-site fallback: persist the changed fields on this device so
      // a reload (offline) still shows them; badged as an offline save.
      const status = err instanceof ApiError ? err.status : undefined
      const staticHost =
        typeof status === 'number' && (status === 404 || status === 405 || status === 501)
      if (err instanceof TypeError || staticHost) {
        writePatientEdits(patient.id, payload as PatientUpdatePayload & Record<string, string>)
        const personPatch: Partial<PatientResponse['person']> = {
          name_ar: payload.name_ar,
          name_latin: payload.name_latin,
          dob: payload.dob,
          sex: payload.sex,
          phone: payload.phone,
        }
        // Drop undefined keys so absent fields don't null-out saved values.
        for (const key of Object.keys(personPatch) as (keyof PatientResponse['person'])[]) {
          if (personPatch[key] === undefined) delete personPatch[key]
        }
        const merged: PatientResponse = {
          ...patient,
          person: { ...patient.person, ...personPatch },
        }
        setPatient(merged)
        setDraft(draftFromPatient(merged))
        setSavedAt(new Date().toISOString())
        setSavedOffline(true)
      } else {
        const detail =
          err instanceof ApiError && err.detail && typeof err.detail === 'object' && 'detail' in err.detail
            ? String((err.detail as { detail: unknown }).detail)
            : err instanceof ApiError
              ? String(err.detail ?? err.status)
              : 'network'
        setSaveError(detail)
      }
    } finally {
      setSaving(false)
    }
  }

  const patientBanner = useMemo(() => {
    if (!patient) return null
    const person = patient.person
    return {
      person: {
        name_ar: person.name_ar ?? '',
        name_latin: person.name_latin ?? '',
        nhi: person.nhi,
        mrn: patient.mrn,
        facility: patient.facility_id,
        dob: person.dob ?? '',
        sex: person.sex ?? '',
        phone: person.phone ?? '',
        address: { city: '', governorate: '' },
        verificationTier: (patient.verification_tier >= 1 && patient.verification_tier <= 4
          ? patient.verification_tier
          : 1) as 1 | 2 | 3 | 4,
      } as const,
      allergies: [],
      vitals: [],
      advanceDirectives: [],
      latestNote: null,
    }
  }, [patient])

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 text-sm font-semibold text-stone-700 transition hover:border-emerald-200 hover:text-emerald-800"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('prov.backToRoster')}
            </button>
            <div>
              <div className="text-sm font-medium text-stone-500">{t('prov.providerChart')}</div>
              <h2 className="text-3xl font-semibold text-stone-950">{t('prov.editableDetail')}</h2>
            </div>
          </div>
          {patient && (
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setScribeOpen(true)}
                data-testid="new-encounter-button"
                className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95"
              >
                <Mic className="h-4 w-4" />
                {t('scribe.newEncounter')}
              </button>
              <span className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-semibold ${toneForTier(patient.verification_tier)}`}>
                <ShieldCheck className="h-4 w-4" />
                {fmt(t('banner.level'), { n: patient.verification_tier, name: tierShortLabel(patient.verification_tier) })}
              </span>
              {loadOffline && <OfflineSampleBadge />}
            </div>
          )}
        </div>
      </section>

      {!loading && loadOffline && (
        <p
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800"
          role="status"
        >
          {t('prov.offlineNote')}
        </p>
      )}

      {loading && (
        <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
          <div className="skeleton h-7 w-72" />
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="skeleton h-24" />
            <div className="skeleton h-24" />
          </div>
        </section>
      )}

      {!loading && loadError && (
        <section className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm font-medium text-red-800 shadow-soft">
          {t('prov.patientLoadFailed')}
        </section>
      )}

      {patientBanner && (
        <PatientBanner {...patientBanner} onAddNote={() => void 0} onViewAllNotes={() => void 0} />
      )}

      {patient && draft && (
        <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
          <div className="mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-primary-700" />
            <h3 className="text-lg font-semibold text-stone-950">{t('prov.identityFields')}</h3>
          </div>
          <p className="text-sm leading-6 text-stone-600">{t('prov.identityEditable')}</p>
          <form onSubmit={submitIdentity} className="mt-4 grid gap-3 sm:grid-cols-2">
            <EditableField
              label={t('prov.nameLatin')}
              value={draft.name_latin}
              onChange={(value) => setDraft({ ...draft, name_latin: value })}
            />
            <EditableField
              label={t('prov.nameAr')}
              value={draft.name_ar}
              onChange={(value) => setDraft({ ...draft, name_ar: value })}
            />
            <EditableField
              label={t('prov.dob')}
              value={draft.dob}
              onChange={(value) => setDraft({ ...draft, dob: value })}
              type="date"
            />
            <label className="grid gap-1.5 text-sm font-medium text-stone-700">
              {t('prov.sex')}
              <select
                value={draft.sex}
                onChange={(event) => setDraft({ ...draft, sex: event.target.value as IdentityDraft['sex'] })}
                className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
              >
                <option value="">—</option>
                <option value="M">{sexLabel('M')}</option>
                <option value="F">{sexLabel('F')}</option>
              </select>
            </label>
            <EditableField
              label={t('prov.phone')}
              value={draft.phone}
              onChange={(value) => setDraft({ ...draft, phone: value })}
            />
            <div className="grid gap-1.5 text-sm font-medium text-stone-700">
              {t('prov.facility')}
              <div className="grid h-11 items-center rounded-lg border border-stone-200 bg-stone-50 px-3 font-mono text-stone-600">
                {patient.facility_id} · {patient.mrn}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95 disabled:opacity-60"
              >
                {saving ? t('prov.saving') : t('prov.saveChanges')}
              </button>
              {savedAt && !saveError && !savedOffline && (
                <span className="text-sm font-semibold text-emerald-800">
                  {t('prov.savedToServer')} · {t('prov.updatedAt')} {savedAt.slice(11, 19)}
                </span>
              )}
              {savedAt && savedOffline && (
                <span
                  className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800"
                  role="status"
                  data-testid="offline-saved-badge"
                >
                  {t('prov.savedOffline')} · {t('prov.updatedAt')} {savedAt.slice(11, 19)}
                </span>
              )}
              {saveError === 'name-required' && (
                <span role="alert" className="text-sm font-semibold text-red-700">
                  {t('prov.nameRequired')}
                </span>
              )}
              {saveError && saveError !== 'name-required' && (
                <span role="alert" className="text-sm font-semibold text-red-700">
                  {t('prov.saveFailed')}: {saveError}
                </span>
              )}
            </div>
          </form>
        </section>
      )}

      {patient && (
        <section className="grid gap-4 xl:grid-cols-2">
          <MedicationsEditor patientId={patient.id} />
          <FamilyHistoryEditor patientId={patient.id} />
        </section>
      )}

      {patient && (
        <section className="grid gap-4 xl:grid-cols-2">
          <VitalsPanel patientId={patient.id} />
          <RecentEncounters patientId={patient.id} refreshKey={encountersRefresh} focusId={encounterFocus} />
        </section>
      )}

      {patient && (
        <section className="grid gap-4 xl:grid-cols-2">
          <Documents patientId={patient.id} />
        </section>
      )}

      {patient && scribeToast && (
        <div
          role="status"
          data-testid="scribe-toast"
          className="fixed bottom-5 start-5 z-50 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900 shadow-lg"
        >
          {t('scribe.toastCommitted')}
        </div>
      )}

      {patient && (
        <SpeakEncounterDock
          patientId={patient.id}
          defaultFacilityId={patient.facility_id}
          open={scribeOpen}
          onClose={() => setScribeOpen(false)}
          onCommitted={(encounter) => {
            // The commit landed a real encounter row — refetch the list and
            // jump straight into its FHIR detail panel.
            setEncountersRefresh((k) => k + 1)
            setEncounterFocus(encounter.id)
            setScribeToast(true)
            window.setTimeout(() => setScribeToast(false), 6000)
          }}
        />
      )}
    </div>
  )
}

function EditableField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
}) {
  return (
    <label className="grid gap-1.5 text-sm font-medium text-stone-700">
      {label}
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
      />
    </label>
  )
}

/* ========================== Term lookup hook ========================== */

function fallbackTermOptions(system: string, query: string, limit = 10) {
  if (system !== 'rxnorm') return []
  const q = query.trim().toLowerCase()
  if (!q) return []
  return RXNORM_FALLBACK
    .filter((term) => term.name.toLowerCase().includes(q) || term.code.includes(q))
    .slice(0, limit)
}

function useTermOptions(system: string, query: string) {
  const [options, setOptions] = useState<{ code: string; name: string }[]>([])

  useEffect(() => {
    let cancelled = false
    const q = query.trim()
    if (!q) {
      setOptions([])
      return
    }
    const handle = window.setTimeout(() => {
      termsApi
        .search(system, q, 10)
        .then((res) => {
          if (cancelled) return
          const mapped = res.results.map((term: TermResult) => ({ code: term.code, name: term.name }))
          setOptions(mapped.length > 0 ? mapped : fallbackTermOptions(system, q))
        })
        .catch(() => {
          // RxNorm remains usable on the deployed static site even when the
          // FastAPI terminology service is unreachable.
          if (!cancelled) setOptions(fallbackTermOptions(system, q))
        })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [system, query])

  return options
}

/* ========================= Medications editor ========================= */

const UCUM_UNITS = ['mg', 'g', 'mL', 'ug', 'mmol', 'IU', '{tablet}', '{capsule}'] as const
const MED_ROUTES = ['oral', 'IV', 'IM', 'SC', 'topical', 'inhalation'] as const
const MED_FORMS = ['tablet', 'capsule', 'solution', 'injection', 'cream', 'inhaler'] as const
const MED_FREQUENCIES = ['QD', 'BID', 'TID', 'QID', 'QHS', 'PRN'] as const

/* ------------------- Offline sample rows (static demo) ------------------- */

/**
 * Bundled contract-exact sample medications shown when the API is unreachable
 * (static pages.dev demo). RxNorm codes are real terms from RXNORM_FALLBACK so
 * the code · name pairing stays clinically coherent. Sample rows describe the
 * shared chart patient id used by SAMPLE_ROSTER.
 */
const SAMPLE_MEDICATIONS: MedicationStatement[] = [
  {
    id: 'sample-med-0001',
    patient_id: SAMPLE_CHART_PATIENT_ID,
    reported_by: 'clinician',
    description: 'Metformin 500 MG Oral Tablet',
    rxnorm_code: '6809',
    status: 'active',
    date_recorded: '2026-08-20',
    source: 'provider-chart',
    note: null,
    dose: '500',
    dose_unit: 'mg',
    route: 'oral',
    form: 'tablet',
    frequency_code: 'BID',
    duration: null,
    quantity: null,
    refills: null,
    indication: 'Type 2 diabetes mellitus',
    prescriber_id: null,
    pharmacy_formulary_notes: null,
    dispense_instructions: null,
    confidence_tier: 2,
    created_at: '2026-08-20T09:00:00Z',
    updated_at: '2026-08-20T09:00:00Z',
  },
  {
    id: 'sample-med-0002',
    patient_id: SAMPLE_CHART_PATIENT_ID,
    reported_by: 'patient',
    description: 'amlodipine',
    rxnorm_code: '17767',
    status: 'active',
    date_recorded: '2026-08-22',
    source: 'patient-portal',
    note: null,
    dose: '5',
    dose_unit: 'mg',
    route: 'oral',
    form: 'tablet',
    frequency_code: 'QD',
    duration: null,
    quantity: null,
    refills: null,
    indication: null,
    prescriber_id: null,
    pharmacy_formulary_notes: null,
    dispense_instructions: null,
    confidence_tier: 2,
    created_at: '2026-08-22T14:30:00Z',
    updated_at: '2026-08-22T14:30:00Z',
  },
]

/**
 * Bundled contract-exact sample family history for the static demo — one
 * relative with a SNOMED-coded condition, mirroring the roster sample's
 * family_history_record_count for the shared chart patient.
 */
const SAMPLE_FAMILY_HISTORY: FamilyHistoryRelative[] = [
  {
    id: 'sample-fh-0001',
    patient_id: SAMPLE_CHART_PATIENT_ID,
    relationship: 'father',
    relationship_code: null,
    relationship_label: null,
    relative_name: 'Mahmoud Al-Khatib',
    relative_birth_year: 1948,
    deceased: false,
    age_at_death: null,
    certainty: 'reported',
    record_status: 'active',
    notes: null,
    conditions: [
      {
        id: 'sample-fhc-0001',
        family_history_relative_id: 'sample-fh-0001',
        snomed_code: '44054006',
        name: 'Diabetes mellitus',
        age_at_onset: 52,
        last_confirmed_at: null,
        needs_reconfirmation: false,
        confirmed_until: null,
      },
    ],
    needs_reconfirmation: false,
  },
]

/**
 * Offline reads: locally saved writes win over the bundled samples, and rows
 * this device tombstoned (status 'offline-deleted', see the offline remove
 * paths) stay hidden.
 */
function mergedOfflineMeds(patientId: string): MedicationStatement[] {
  const local = readOfflineMeds(patientId)
  const hidden = new Set(
    local.filter((item) => item.status === 'offline-deleted').map((item) => item.id),
  )
  const visible = local.filter((item) => item.status !== 'offline-deleted')
  const ids = new Set(visible.map((item) => item.id))
  return [...visible, ...SAMPLE_MEDICATIONS.filter((item) => !ids.has(item.id) && !hidden.has(item.id))]
}

function mergedOfflineFamilyHistory(patientId: string): FamilyHistoryRelative[] {
  const local = readOfflineFamilyHistory(patientId)
  const hidden = new Set(
    local.filter((item) => item.record_status === 'offline-deleted').map((item) => item.id),
  )
  const visible = local.filter((item) => item.record_status !== 'offline-deleted')
  const ids = new Set(visible.map((item) => item.id))
  return [...visible, ...SAMPLE_FAMILY_HISTORY.filter((item) => !ids.has(item.id) && !hidden.has(item.id))]
}

const isSampleMedication = (id: string) => SAMPLE_MEDICATIONS.some((item) => item.id === id)
const isSampleFamilyHistory = (id: string) => SAMPLE_FAMILY_HISTORY.some((item) => item.id === id)

/**
 * Offline delete of a bundled sample row — the sample catalog is immutable, so
 * removal is persisted as a tombstone (the same row flagged offline-deleted)
 * that the merge functions above keep filtering out on this device.
 */
function tombstoneOfflineMed(patientId: string, id: string): void {
  const local = readOfflineMeds(patientId)
  if (local.some((item) => item.id === id)) return
  const original = SAMPLE_MEDICATIONS.find((item) => item.id === id)
  if (!original) return
  writeOfflineMeds(patientId, [
    ...local,
    { ...original, id, status: 'offline-deleted', updated_at: new Date().toISOString() },
  ])
}

function tombstoneOfflineFamilyHistory(patientId: string, id: string): void {
  const local = readOfflineFamilyHistory(patientId)
  if (local.some((item) => item.id === id)) return
  const original = SAMPLE_FAMILY_HISTORY.find((item) => item.id === id)
  if (!original) return
  writeOfflineFamilyHistory(patientId, [
    ...local,
    { ...original, id, record_status: 'offline-deleted' },
  ])
}

function MedicationsEditor({ patientId }: { patientId: string }) {
  const [items, setItems] = useState<MedicationStatement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [offline, setOffline] = useState(false)
  const [query, setQuery] = useState('')
  const [rxnormCode, setRxnormCode] = useState('')
  const [description, setDescription] = useState('')
  const [dose, setDose] = useState('')
  const [doseUnit, setDoseUnit] = useState('')
  const [route, setRoute] = useState('')
  const [form, setForm] = useState('')
  const [frequency, setFrequency] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const options = useTermOptions('rxnorm', query)

  function refresh(signal?: AbortSignal) {
    setLoading(true)
    setError(false)
    listPatientMedications(patientId, { signal })
      .then((res) => {
        // Real API data wins when the backend is reachable.
        setOffline(false)
        setItems(res.items)
      })
      .catch((err: unknown) => {
        // Static-site fallback: local medications only (no fabricated clinical
        // content) — an empty store still shows the honest empty state.
        const status = err instanceof ApiError ? err.status : undefined
        const staticHost =
          typeof status === 'number' && (status === 404 || status === 405 || status === 501)
        if (err instanceof TypeError || staticHost) {
          // Static-site fallback: locally saved rows + bundled samples,
          // local writes winning — badged as sample data.
          setOffline(true)
          setItems(mergedOfflineMeds(patientId))
        } else {
          setError(true)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const controller = new AbortController()
    refresh(controller.signal)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!description.trim()) return
    setSubmitting(true)
    const payload = {
      patient_id: patientId,
      reported_by: 'clinician' as const,
      description: description.trim(),
      rxnorm_code: rxnormCode || null,
      status: 'active',
      date_recorded: new Date().toISOString().slice(0, 10),
      source: 'provider-chart',
      dose: dose.trim() || null,
      dose_unit: doseUnit || null,
      route: route || null,
      form: form || null,
      frequency_code: frequency || null,
    }
    try {
      // Clinician-provenance discrete SIG: route/form/frequency + UCUM unit are
      // separate typed fields, never concatenated into free text.
      if (offline) {
        // Known-offline: create locally + persist to localStorage.
        const created: OfflineMedication = {
          ...payload,
          note: null,
          duration: null,
          quantity: null,
          refills: null,
          indication: null,
          prescriber_id: null,
          pharmacy_formulary_notes: null,
          dispense_instructions: null,
          id: cryptoId(),
          confidence_tier: 2,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }
        writeOfflineMeds(patientId, [...readOfflineMeds(patientId), created])
        setItems(mergedOfflineMeds(patientId))
      } else {
        try {
          await createMedication(payload)
        } catch (err) {
          // Write failed against a static host: persist locally instead so the
          // demo keeps working and the row survives a reload.
          const status = err instanceof ApiError ? err.status : undefined
          const staticHost =
            typeof status === 'number' && (status === 404 || status === 405 || status === 501)
          if (!(err instanceof TypeError || staticHost)) throw err
          setOffline(true)
          const created: OfflineMedication = {
            ...payload,
            note: null,
            duration: null,
            quantity: null,
            refills: null,
            indication: null,
            prescriber_id: null,
            pharmacy_formulary_notes: null,
            dispense_instructions: null,
            id: cryptoId(),
            confidence_tier: 2,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }
          writeOfflineMeds(patientId, [...readOfflineMeds(patientId), created])
        }
        refresh()
      }
      setQuery('')
      setRxnormCode('')
      setDescription('')
      setDose('')
      setDoseUnit('')
      setRoute('')
      setForm('')
      setFrequency('')
    } finally {
      setSubmitting(false)
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('prov.med.deleteConfirm'))) return
    if (offline) {
      if (isSampleMedication(id) && !readOfflineMeds(patientId).some((item) => item.id === id)) {
        // Sample rows are immutable — persist a tombstone so the merge keeps
        // hiding the row on this device.
        tombstoneOfflineMed(patientId, id)
        setItems(mergedOfflineMeds(patientId))
        return
      }
      const next = readOfflineMeds(patientId).filter((item) => item.id !== id)
      writeOfflineMeds(patientId, next)
      setItems(mergedOfflineMeds(patientId))
      return
    }
    try {
      await deleteMedication(id)
    } catch (err) {
      const status = err instanceof ApiError ? err.status : undefined
      const staticHost =
        typeof status === 'number' && (status === 404 || status === 405 || status === 501)
      if (!(err instanceof TypeError || staticHost)) throw err
      setOffline(true)
      tombstoneOfflineMed(patientId, id)
      setItems(mergedOfflineMeds(patientId))
      return
    }
    refresh()
  }

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
      <div className="mb-4 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-800">
          <Pill className="h-4 w-4" />
        </span>
        <h3 className="text-lg font-semibold text-stone-950">{t('prov.med.title')}</h3>
        {offline && <OfflineSampleBadge />}
      </div>

      <form onSubmit={submit} className="grid gap-3 md:grid-cols-2">
        <label className="grid gap-1.5 text-sm font-medium text-stone-700 md:col-span-2">
          {t('prov.med.rxnorm')}
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={fmt(t('prov.searchTerms'), { title: t('prov.med.title').toLowerCase() })}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
        <label className="grid gap-1.5 text-sm font-medium text-stone-700 md:col-span-2">
          {t('prov.suggestion')}
          <select
            value={rxnormCode}
            onChange={(event) => {
              const picked = options.find((option) => option.code === event.target.value)
              setRxnormCode(event.target.value)
              if (picked) setDescription(picked.name)
            }}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          >
            <option value="">{t('prov.selectTermPlaceholder')}</option>
            {options.map((option) => (
              <option key={option.code} value={option.code}>
                {option.code} · {demoText(option.name)}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1.5 text-sm font-medium text-stone-700 md:col-span-2">
          {t('prov.med.description')}
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
        <EditableField label={t('prov.med.dose')} value={dose} onChange={setDose} />
        <SelectField label={t('prov.med.doseUnit')} value={doseUnit} onChange={setDoseUnit} options={UCUM_UNITS} translate />
        <SelectField label={t('prov.med.route')} value={route} onChange={setRoute} options={MED_ROUTES} translate />
        <SelectField label={t('prov.med.form')} value={form} onChange={setForm} options={MED_FORMS} translate />
        <SelectField label={t('prov.med.frequency')} value={frequency} onChange={setFrequency} options={MED_FREQUENCIES} translate />
        <div className="grid gap-1.5 text-sm font-medium text-stone-700">
          {t('prov.med.reportedBy')}
          <div className="grid h-11 items-center rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-sm font-semibold text-emerald-900">
            {t('prov.med.clinician')}
          </div>
        </div>
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={submitting || !description.trim()}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {t('prov.med.add')}
          </button>
        </div>
      </form>

      <div className="mt-4 overflow-hidden rounded-lg border border-stone-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-stone-50 text-stone-500">
            <tr>
              <th className="px-4 py-3 font-medium">{t('prov.code')}</th>
              <th className="px-4 py-3 font-medium">{t('prov.med.description')}</th>
              <th className="px-4 py-3 font-medium">{t('prov.statusDose')}</th>
              <th className="px-4 py-3 font-medium">{t('prov.med.reportedBy')}</th>
              <th className="px-4 py-3 font-medium" aria-label={t('prov.delete')} />
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-stone-500">…</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-red-700">
                  {t('prov.med.loadFailed')}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-stone-500">
                  {t('prov.med.empty')}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-emerald-800">
                    {item.rxnorm_code ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-stone-800">{demoText(item.description)}</td>
                  <td className="px-4 py-3 text-stone-600">
                    {[item.dose && `${item.dose} ${item.dose_unit ? demoText(item.dose_unit) : ''}`.trim(), demoText(item.route ?? ''), demoText(item.form ?? ''), demoText(item.frequency_code ?? '')]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-stone-600">
                    {item.reported_by === 'clinician' ? t('prov.med.clinician') : t('prov.med.patient')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void remove(item.id)}
                      aria-label={`${t('prov.delete')} ${item.description}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 text-stone-500 transition hover:border-red-200 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function SelectField({
  label,
  value,
  onChange,
  options,
  translate = false,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: readonly string[]
  translate?: boolean
}) {
  return (
    <label className="grid gap-1.5 text-sm font-medium text-stone-700">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {translate ? demoText(option) : option}
          </option>
        ))}
      </select>
    </label>
  )
}

/* ======================== Family history editor ======================== */

const FH_RELATIONSHIPS = [
  'father',
  'mother',
  'brother',
  'sister',
  'son',
  'daughter',
  'paternal_grandfather',
  'paternal_grandmother',
  'maternal_grandfather',
  'maternal_grandmother',
  'uncle',
  'aunt',
] as const

function FamilyHistoryEditor({ patientId }: { patientId: string }) {
  const [items, setItems] = useState<FamilyHistoryRelative[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [offline, setOffline] = useState(false)
  const [relationship, setRelationship] = useState('')
  const [relativeName, setRelativeName] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [conditionQuery, setConditionQuery] = useState('')
  const [conditionCode, setConditionCode] = useState('')
  const [conditionName, setConditionName] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const options = useTermOptions('snomed-family-history', conditionQuery)

  function refresh(signal?: AbortSignal) {
    setLoading(true)
    setError(false)
    listFamilyHistory(patientId, { signal })
      .then((res) => {
        // Real API data wins when the backend is reachable.
        setOffline(false)
        setItems(res.items)
      })
      .catch((err: unknown) => {
        // Static-site fallback: local rows only — empty store → honest empty state.
        const status = err instanceof ApiError ? err.status : undefined
        const staticHost =
          typeof status === 'number' && (status === 404 || status === 405 || status === 501)
        if (err instanceof TypeError || staticHost) {
          // Static-site fallback: locally saved rows + bundled samples,
          // local writes winning — badged as sample data.
          setOffline(true)
          setItems(mergedOfflineFamilyHistory(patientId))
        } else {
          setError(true)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const controller = new AbortController()
    refresh(controller.signal)
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!relationship) return
    if (!conditionName.trim()) {
      // ≥1 condition enforced client-side; the API rejects 0-condition records.
      setFormError('condition-required')
      return
    }
    setFormError(null)
    setSubmitting(true)
    const conditions = [
      {
        snomed_code: conditionCode || 'UNK',
        name: conditionName.trim(),
      },
    ]
    const buildOfflineRelative = (): OfflineFamilyHistoryRelative => {
      // Static-site fallback: create locally + persist to localStorage.
      const relativeId = cryptoId()
      return {
        id: relativeId,
        patient_id: patientId,
        relationship,
        relationship_code: null,
        relationship_label: null,
        relative_name: relativeName.trim() || null,
        relative_birth_year: birthYear ? Number(birthYear) : null,
        deceased: false,
        age_at_death: null,
        certainty: 'reported',
        record_status: 'active',
        notes: null,
        needs_reconfirmation: false,
        conditions: conditions.map((condition) => ({
          id: cryptoId(),
          family_history_relative_id: relativeId,
          snomed_code: condition.snomed_code,
          name: condition.name,
          age_at_onset: null,
          last_confirmed_at: null,
          needs_reconfirmation: false,
          confirmed_until: null,
        })),
      }
    }
    try {
      if (offline) {
        const created = buildOfflineRelative()
        writeOfflineFamilyHistory(patientId, [...readOfflineFamilyHistory(patientId), created])
        setItems(mergedOfflineFamilyHistory(patientId))
      } else {
        try {
          await addFamilyHistoryRelative(patientId, {
            relationship,
            relative_name: relativeName.trim() || null,
            relative_birth_year: birthYear ? Number(birthYear) : null,
            conditions,
          })
        } catch (err) {
          // Write failed against a static host: persist locally instead so the
          // demo keeps working and the row survives a reload.
          const status = err instanceof ApiError ? err.status : undefined
          const staticHost =
            typeof status === 'number' && (status === 404 || status === 405 || status === 501)
          if (!(err instanceof TypeError || staticHost)) throw err
          setOffline(true)
          const created = buildOfflineRelative()
          writeOfflineFamilyHistory(patientId, [...readOfflineFamilyHistory(patientId), created])
        }
        refresh()
      }
      setRelationship('')
      setRelativeName('')
      setBirthYear('')
      setConditionQuery('')
      setConditionCode('')
      setConditionName('')
    } finally {
      setSubmitting(false)
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t('prov.fh.deleteConfirm'))) return
    if (offline) {
      if (isSampleFamilyHistory(id) && !readOfflineFamilyHistory(patientId).some((item) => item.id === id)) {
        // Sample rows are immutable — persist a tombstone so the merge keeps
        // hiding the row on this device.
        tombstoneOfflineFamilyHistory(patientId, id)
        setItems(mergedOfflineFamilyHistory(patientId))
        return
      }
      const next = readOfflineFamilyHistory(patientId).filter((item) => item.id !== id)
      writeOfflineFamilyHistory(patientId, next)
      setItems(mergedOfflineFamilyHistory(patientId))
      return
    }
    try {
      await deleteFamilyHistoryRelative(id)
    } catch (err) {
      const status = err instanceof ApiError ? err.status : undefined
      const staticHost =
        typeof status === 'number' && (status === 404 || status === 405 || status === 501)
      if (!(err instanceof TypeError || staticHost)) throw err
      setOffline(true)
      tombstoneOfflineFamilyHistory(patientId, id)
      setItems(mergedOfflineFamilyHistory(patientId))
      return
    }
    refresh()
  }

  return (
    <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-soft">
      <div className="mb-4 flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-800">
          <Users className="h-4 w-4" />
        </span>
        <h3 className="text-lg font-semibold text-stone-950">{t('prov.fh.title')}</h3>
        {offline && <OfflineSampleBadge />}
      </div>

      <form onSubmit={submit} className="grid gap-3 md:grid-cols-2">
        <SelectField label={t('prov.fh.relationship')} value={relationship} onChange={setRelationship} options={FH_RELATIONSHIPS} translate />
        <EditableField label={t('prov.fh.relativeName')} value={relativeName} onChange={setRelativeName} />
        <EditableField label={t('prov.fh.birthYear')} value={birthYear} onChange={setBirthYear} type="number" />
        <label className="grid gap-1.5 text-sm font-medium text-stone-700">
          {t('prov.fh.condition')}
          <input
            value={conditionQuery}
            onChange={(event) => setConditionQuery(event.target.value)}
            placeholder={fmt(t('prov.searchTerms'), { title: t('prov.fh.title').toLowerCase() })}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition placeholder:text-stone-400 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
        <label className="grid gap-1.5 text-sm font-medium text-stone-700 md:col-span-2">
          {t('prov.suggestion')}
          <select
            value={conditionCode}
            onChange={(event) => {
              const picked = options.find((option) => option.code === event.target.value)
              setConditionCode(event.target.value)
              if (picked) setConditionName(picked.name)
            }}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          >
            <option value="">{t('prov.selectTermPlaceholder')}</option>
            {options.map((option) => (
              <option key={option.code} value={option.code}>
                {option.code} · {demoText(option.name)}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1.5 text-sm font-medium text-stone-700 md:col-span-2">
          {t('prov.name')}
          <input
            value={conditionName}
            onChange={(event) => setConditionName(event.target.value)}
            className="h-11 rounded-lg border border-stone-200 bg-white px-3 text-stone-950 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
          />
        </label>
        <div className="flex items-center gap-3 md:col-span-2">
          <button
            type="submit"
            disabled={submitting || !relationship}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary-700 px-4 text-sm font-semibold text-white shadow-md shadow-emerald-900/15 transition hover:bg-primary-800 active:scale-95 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {t('prov.fh.add')}
          </button>
          {formError === 'condition-required' && (
            <span role="alert" className="text-sm font-semibold text-red-700">
              {t('prov.fh.conditionRequired')}
            </span>
          )}
        </div>
      </form>

      <div className="mt-4 overflow-hidden rounded-lg border border-stone-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-stone-50 text-stone-500">
            <tr>
              <th className="px-4 py-3 font-medium">{t('prov.fh.relationship')}</th>
              <th className="px-4 py-3 font-medium">{t('prov.fh.relativeName')}</th>
              <th className="px-4 py-3 font-medium">{t('prov.fh.birthYear')}</th>
              <th className="px-4 py-3 font-medium">{t('prov.fh.condition')}</th>
              <th className="px-4 py-3 font-medium" aria-label={t('prov.delete')} />
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-stone-500">…</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-red-700">
                  {t('prov.fh.loadFailed')}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-stone-500">
                  {t('prov.fh.empty')}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td className="px-4 py-3 text-stone-800">{demoText(item.relationship)}</td>
                  <td className="px-4 py-3 text-stone-800">{item.relative_name ?? '—'}</td>
                  <td className="px-4 py-3 text-stone-600">{item.relative_birth_year ?? '—'}</td>
                  <td className="px-4 py-3 text-stone-600">
                    {item.conditions.map((condition) => (
                      <div key={condition.id}>
                        <span className="font-mono text-emerald-800">{condition.snomed_code}</span>{' '}
                        {demoText(condition.name)}
                      </div>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => void remove(item.id)}
                      aria-label={`${t('prov.delete')} ${item.relative_name ?? item.relationship}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 text-stone-500 transition hover:border-red-200 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  )
}
